'use client';
import * as React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '../cn';
import { Button } from './button';
import { IconButton } from './button';

export interface SheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  /** right = slide-over panel (default). center = modal card. */
  side?: 'right' | 'center';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

const widths = { sm: 'max-w-[420px]', md: 'max-w-[560px]', lg: 'max-w-[720px]', xl: 'max-w-[960px]' };

export function Sheet({ open, onOpenChange, title, description, children, footer, side = 'right', size = 'md', className }: SheetProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="ui-overlay fixed inset-0 z-50 bg-black/25 backdrop-blur-[2px]" />
        <Dialog.Content
          className={cn(
            'fixed z-50 flex flex-col bg-surface shadow-[var(--shadow-lg)] focus:outline-none',
            side === 'right'
              ? cn('ui-sheet top-2 right-2 bottom-2 w-[calc(100vw-16px)] rounded-[var(--radius-xl)]', widths[size])
              : cn('ui-modal left-1/2 top-1/2 w-[calc(100vw-32px)] max-h-[calc(100vh-32px)] rounded-[var(--radius-xl)]', widths[size]),
            className,
          )}
        >
          <div className="flex items-start justify-between gap-4 px-6 pt-5 pb-4 hairline-b">
            <div className="min-w-0">
              <Dialog.Title className="text-[17px] font-semibold tracking-[-0.01em] text-text">{title}</Dialog.Title>
              {description ? <Dialog.Description className="text-[13px] text-text-2 mt-0.5">{description}</Dialog.Description> : <Dialog.Description className="sr-only">Dialog</Dialog.Description>}
            </div>
            <Dialog.Close asChild>
              <IconButton label="Close" size="sm" variant="secondary" className="shrink-0 -mr-1 -mt-0.5">
                <X />
              </IconButton>
            </Dialog.Close>
          </div>
          <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
          {footer ? <div className="flex items-center justify-end gap-2 px-6 py-4 hairline-t bg-surface-2 rounded-b-[var(--radius-xl)]">{footer}</div> : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  loading?: boolean;
  onConfirm: () => void | Promise<void>;
  children?: React.ReactNode;
}

export function ConfirmDialog({ open, onOpenChange, title, description, confirmLabel = 'Confirm', cancelLabel = 'Cancel', destructive, loading, onConfirm, children }: ConfirmDialogProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="ui-overlay fixed inset-0 z-50 bg-black/25 backdrop-blur-[2px]" />
        <Dialog.Content className="ui-modal fixed z-50 left-1/2 top-1/2 w-[calc(100vw-32px)] max-w-[400px] bg-surface rounded-[var(--radius-xl)] shadow-[var(--shadow-lg)] p-6 focus:outline-none">
          <Dialog.Title className="text-[17px] font-semibold text-text text-center">{title}</Dialog.Title>
          {description ? <Dialog.Description className="text-[13px] text-text-2 mt-2 text-center">{description}</Dialog.Description> : <Dialog.Description className="sr-only">Confirm</Dialog.Description>}
          {children ? <div className="mt-4">{children}</div> : null}
          <div className="grid grid-cols-2 gap-2 mt-6">
            <Dialog.Close asChild>
              <Button variant="secondary" size="lg">
                {cancelLabel}
              </Button>
            </Dialog.Close>
            <Button variant={destructive ? 'destructive' : 'primary'} size="lg" loading={loading} onClick={() => void onConfirm()}>
              {confirmLabel}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
